// src/pages/EmailMarketingPage.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Modal,
  ProgressBar,
  ListGroup
} from "react-bootstrap";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { t } from "../utils/translations.js";
import { callXeroFunction } from "../firebase.js";

export default function EmailMarketingPage() {
  const { user } = useAuth();
  const { language } = useLanguage();

  // Form state
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [testEmail, setTestEmail] = useState("");

  // Clients state
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);

  // Sending state
  const [sending, setSending] = useState(false);
  const [sendingProgress, setSendingProgress] = useState(0);
  const [result, setResult] = useState(null);

  // Preview modal
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Load clients on mount
  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await callXeroFunction('massEmailPreview', {}, { method: 'GET' });
        setClients(response.clients || []);
      } catch (error) {
        console.error("Error loading clients:", error);
      } finally {
        setLoadingClients(false);
      }
    };

    if (user?.isAdmin) {
      loadClients();
    }
  }, [user]);

  // Send test email
  const handleSendTest = async () => {
    if (!subject.trim() || !content.trim()) {
      alert(t(language, "emailFillSubjectContent"));
      return;
    }

    if (!testEmail.trim()) {
      alert(t(language, "emailFillTestEmail"));
      return;
    }

    setSending(true);
    setResult(null);

    try {
      await callXeroFunction('massEmail', {
        subject,
        htmlContent: formatContentToHTML(content),
        testEmail: testEmail.trim()
      }, { method: 'POST' });

      setResult({
        success: true,
        message: t(language, "emailTestSent").replace("{email}", testEmail)
      });
    } catch (error) {
      setResult({
        success: false,
        message: error.message
      });
    } finally {
      setSending(false);
    }
  };

  // Send to all clients or retry failed ones
  const handleSendAll = async (retryEmails = null) => {
    setShowConfirm(false);
    setSending(true);
    setResult(null);
    setSendingProgress(0);

    const progressInterval = setInterval(() => {
      setSendingProgress(prev => {
        if (prev >= 90) return prev;
        return prev + (90 - prev) * 0.05;
      });
    }, 2000);

    try {
      const payload = {
        subject,
        htmlContent: formatContentToHTML(content)
      };

      if (retryEmails && retryEmails.length > 0) {
        payload.onlyEmails = retryEmails;
      }

      const response = await callXeroFunction('massEmail', payload, { method: 'POST' });

      clearInterval(progressInterval);
      setSendingProgress(100);

      setResult({
        success: true,
        sent: response.sent,
        failed: response.failed,
        total: response.total,
        errors: response.errors,
        isRetry: !!retryEmails
      });
    } catch (error) {
      clearInterval(progressInterval);
      setResult({
        success: false,
        message: error.message
      });
    } finally {
      setSending(false);
    }
  };

  // Retry sending to failed emails
  const handleRetryFailed = () => {
    if (result?.errors && result.errors.length > 0) {
      const failedEmails = result.errors.map(e => e.email);
      handleSendAll(failedEmails);
    }
  };

  // Format plain text content to HTML
  const formatContentToHTML = (text) => {
    const paragraphs = text.split('\n\n').filter(p => p.trim());
    return paragraphs.map(p => {
      const withBreaks = p.replace(/\n/g, '<br>');
      return `<p style="margin: 0 0 18px 0; color: #4a4a4a; font-size: 16px; line-height: 1.8;">${withBreaks}</p>`;
    }).join('');
  };

  // Generate preview HTML
  const getPreviewHTML = () => {
    const firstName = "João";
    const htmlContent = formatContentToHTML(content).replace(/\{nome\}/gi, firstName);

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
      </head>
      <body style="font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.8; color: #4a4a4a; margin: 0; padding: 20px; background-color: #fdf2f4;">
        <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.1);">
          <div style="background: linear-gradient(135deg, #e91e63 0%, #f06292 50%, #f8bbd9 100%); padding: 40px 20px; text-align: center;">
            <h1 style="color: #ffffff; margin: 0; font-size: 36px; font-weight: 300; letter-spacing: 3px; text-shadow: 0 2px 4px rgba(0,0,0,0.1);">IT'S SO CLEAN</h1>
            <p style="color: #fce4ec; margin: 10px 0 0 0; font-size: 14px; letter-spacing: 2px; text-transform: uppercase;">Professional Cleaning Services</p>
          </div>
          <div style="padding: 40px 35px; background-color: #ffffff;">
            ${htmlContent}
          </div>
          <div style="background: linear-gradient(135deg, #880e4f 0%, #ad1457 100%); padding: 35px 20px; text-align: center;">
            <p style="color: #ffffff; margin: 0 0 15px 0; font-size: 18px; font-weight: 500;">It's So Clean</p>
            <p style="color: #f8bbd9; margin: 0 0 8px 0; font-size: 14px;">North Shore, Auckland, New Zealand</p>
            <p style="color: #f8bbd9; margin: 0 0 8px 0; font-size: 14px;">info@itssoclean.co.nz</p>
            <p style="color: #f8bbd9; margin: 0 0 20px 0; font-size: 14px;">www.itssoclean.co.nz</p>
            <div style="border-top: 1px solid rgba(255,255,255,0.2); padding-top: 20px; margin-top: 10px;">
              <p style="color: #f48fb1; margin: 0; font-size: 12px;">&copy; ${new Date().getFullYear()} It's So Clean. All rights reserved.</p>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
  };

  if (!user?.isAdmin) {
    return (
      <Container className="py-5 text-center">
        <h5>{t(language, "emailAccessDenied")}</h5>
        <p className="text-muted">
          {t(language, "emailAccessDeniedDesc")}
        </p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1" style={{ color: "#e91e63" }}>
            {t(language, "emailTitle")}
          </h2>
          <p className="text-muted mb-0">
            {t(language, "emailSubtitle")}
          </p>
        </div>
        {!loadingClients && (
          <Badge style={{ backgroundColor: "#e91e63" }} className="fs-6 px-3 py-2">
            {clients.length} {t(language, "emailClientsWithEmail")}
          </Badge>
        )}
      </div>

      {/* Result Alert */}
      {result && (
        <Alert
          variant={result.success ? "success" : "danger"}
          dismissible
          onClose={() => setResult(null)}
          className="mb-4"
        >
          {result.message ? (
            result.message
          ) : (
            <>
              <strong>
                {t(language, "emailCampaignComplete")}
              </strong>
              <br />
              {t(language, "emailSentFailed")
                .replace("{sent}", result.sent)
                .replace("{failed}", result.failed)
                .replace("{total}", result.total)}
              {result.errors && result.errors.length > 0 && (
                <>
                  <div className="mt-3 d-flex gap-2 align-items-center">
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={handleRetryFailed}
                      disabled={sending}
                    >
                      {t(language, "emailRetryFailed").replace("{count}", result.errors.length)}
                    </Button>
                    <small className="text-muted">
                      {t(language, "emailRetryWait")}
                    </small>
                  </div>
                  <details className="mt-2">
                    <summary className="cursor-pointer">
                      {t(language, "emailViewErrors")}
                    </summary>
                    <ul className="mb-0 mt-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                      {result.errors.map((err, i) => (
                        <li key={i}><small>{err.email}: {err.error}</small></li>
                      ))}
                    </ul>
                  </details>
                </>
              )}
            </>
          )}
        </Alert>
      )}

      <Row>
        {/* Email Composer */}
        <Col lg={8}>
          <Card className="mb-4 shadow-sm" style={{ borderColor: "#f8bbd9" }}>
            <Card.Header style={{ background: "linear-gradient(135deg, #e91e63 0%, #f06292 100%)", color: "white" }}>
              <h5 className="mb-0">
                {t(language, "emailCompose")}
              </h5>
            </Card.Header>
            <Card.Body style={{ backgroundColor: "#fdf8f9" }}>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>{t(language, "emailSubject")}</strong> <span style={{ color: "#e91e63" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t(language, "emailSubjectPlaceholder")}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    disabled={sending}
                    style={{ borderColor: "#f8bbd9" }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>{t(language, "emailContent")}</strong> <span style={{ color: "#e91e63" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={12}
                    placeholder={t(language, "emailContentPlaceholder")}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    disabled={sending}
                    style={{
                      fontFamily: "'Segoe UI', Arial, sans-serif",
                      lineHeight: 1.8,
                      fontSize: "16px",
                      borderColor: "#f8bbd9",
                      backgroundColor: "#ffffff",
                      color: "#4a4a4a"
                    }}
                  />
                  <Form.Text style={{ color: "#ad1457" }}>
                    {t(language, "emailContentTip")}
                  </Form.Text>
                </Form.Group>

                {/* Progress bar when sending */}
                {sending && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small style={{ color: "#ad1457" }}>{t(language, "emailSendingProgress")}</small>
                      <small style={{ color: "#ad1457" }}>{Math.round(sendingProgress)}%</small>
                    </div>
                    <ProgressBar
                      animated
                      striped
                      now={sendingProgress}
                      style={{ backgroundColor: "#fce4ec" }}
                    >
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        style={{ width: `${sendingProgress}%`, backgroundColor: "#e91e63" }}
                      />
                    </ProgressBar>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <Button
                    style={{ backgroundColor: "transparent", borderColor: "#e91e63", color: "#e91e63" }}
                    onClick={() => setShowPreview(true)}
                    disabled={!content.trim() || sending}
                  >
                    {t(language, "emailPreview")}
                  </Button>

                  <div className="d-flex align-items-center gap-2 flex-grow-1">
                    <Form.Control
                      type="email"
                      placeholder={t(language, "emailTestAddress")}
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      disabled={sending}
                      style={{ maxWidth: "250px", borderColor: "#f8bbd9" }}
                    />
                    <Button
                      style={{ backgroundColor: "#f06292", borderColor: "#f06292", color: "white" }}
                      onClick={handleSendTest}
                      disabled={!subject.trim() || !content.trim() || !testEmail.trim() || sending}
                    >
                      {sending ? <Spinner size="sm" /> : "🧪"} Test
                    </Button>
                  </div>

                  <Button
                    style={{ backgroundColor: "#e91e63", borderColor: "#e91e63", color: "white" }}
                    size="lg"
                    onClick={() => setShowConfirm(true)}
                    disabled={!subject.trim() || !content.trim() || sending || clients.length === 0}
                  >
                    {sending ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {t(language, "invoiceSending")}
                      </>
                    ) : (
                      <>
                        {t(language, "emailSendToClients").replace("{count}", clients.length)}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Client List */}
        <Col lg={4}>
          <Card className="shadow-sm" style={{ borderColor: "#f8bbd9" }}>
            <Card.Header style={{ background: "linear-gradient(135deg, #880e4f 0%, #ad1457 100%)", color: "white" }}>
              <h5 className="mb-0">
                {t(language, "emailRecipients")}
              </h5>
            </Card.Header>
            <Card.Body style={{ maxHeight: "500px", overflowY: "auto", padding: 0, backgroundColor: "#fdf8f9" }}>
              {loadingClients ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" style={{ color: "#e91e63" }} />
                </div>
              ) : clients.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  {t(language, "emailNoClients")}
                </div>
              ) : (
                <ListGroup variant="flush">
                  {clients.map((client) => (
                    <ListGroup.Item key={client.id} className="py-2">
                      <div className="fw-bold" style={{ fontSize: "0.9rem" }}>{client.name}</div>
                      <small className="text-muted">{client.email}</small>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {t(language, "emailPreviewTitle")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, backgroundColor: "#f4f4f4" }}>
          <div
            dangerouslySetInnerHTML={{ __html: getPreviewHTML() }}
            style={{ maxHeight: "70vh", overflowY: "auto" }}
          />
        </Modal.Body>
        <Modal.Footer>
          <small className="text-muted me-auto">
            {t(language, "emailPreviewNote")}
          </small>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            {t(language, "close")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {t(language, "emailConfirmTitle")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {t(language, "emailConfirmMsg").replace("{count}", clients.length)}
          </p>
          <Alert variant="warning">
            <strong>{t(language, "emailConfirmSubject")}</strong> {subject}
            <br />
            <strong>{t(language, "emailConfirmContent")}</strong>
            <br />
            <small className="text-muted">
              {content.substring(0, 150)}{content.length > 150 ? "..." : ""}
            </small>
          </Alert>
          <p className="text-muted small mb-0">
            {t(language, "emailConfirmWarning")}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            {t(language, "cancel")}
          </Button>
          <Button variant="success" onClick={handleSendAll}>
            {t(language, "emailConfirmSend").replace("{count}", clients.length)}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
